import { useEffect, useState, type ReactNode } from 'react';
import type { OperationSchedule } from '@aeronexo/contracts';
import type { OperationDetail } from './OperationWorkflow.js';
import './operation-report.css';

interface Report {
  reportVersion: string;
  generatedAt: string;
  organization: { id: string; name: string };
  operation: OperationDetail;
  schedule: OperationSchedule;
}
const labels: Record<string, string> = {
  draft: 'Rascunho',
  planned: 'Planejada',
  coordinating: 'Em coordenação',
  ready: 'Coordenação concluída',
  closed: 'Encerrada',
  cancelled: 'Cancelada',
  not_started: 'Não iniciada',
  in_progress: 'Em execução',
  completed: 'Concluída',
  authorized: 'Autorizada',
  not_authorized: 'Não autorizada',
  revoked: 'Revogada',
  reopened: 'Reaberta',
  started: 'Início da execução',
  departed: 'Partida',
  arrived: 'Chegada',
  organization_administrator: 'Administrador da organização',
  coordinator: 'Coordenador',
  open: 'Aberta',
  resolved: 'Resolvida',
  informational: 'Informativa',
  warning: 'Atenção',
  critical: 'Crítica',
  delay: 'Atraso',
  diversion: 'Alternância',
  cancellation: 'Cancelamento',
  technical: 'Técnica',
  weather: 'Meteorologia',
  passenger: 'Passageiro',
  other: 'Outra',
};
const label = (value: string) => labels[value] ?? value;
function ReportSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="reportSection">
      <h4>{title}</h4>
      {children}
    </section>
  );
}
export function OperationReport({
  organizationId,
  operationId,
  active,
  revision,
  onExpiredSession,
}: {
  organizationId: string;
  operationId: string;
  active: boolean;
  revision: string;
  onExpiredSession: () => Promise<void>;
}) {
  const [report, setReport] = useState<Report>();
  const [error, setError] = useState('');
  const [refresh, setRefresh] = useState(0);
  useEffect(() => {
    if (!active) return;
    const controller = new AbortController();
    setReport(undefined);
    setError('');
    void (async () => {
      try {
        const response = await fetch(
          `/v1/organizations/${organizationId}/operations/${operationId}/report`,
          { credentials: 'include', signal: controller.signal },
        );
        if (response.status === 401) {
          await onExpiredSession();
          return;
        }
        if (!response.ok)
          throw new Error(
            response.status === 403
              ? 'Seu perfil não permite consultar este relatório.'
              : 'Não foi possível emitir o relatório. Tente novamente.',
          );
        const result = (await response.json()) as Report;
        if (!controller.signal.aborted) setReport(result);
      } catch (failure) {
        if (!controller.signal.aborted)
          setError(failure instanceof Error ? failure.message : 'Falha ao emitir o relatório.');
      }
    })();
    return () => controller.abort();
  }, [organizationId, operationId, active, revision, refresh, onExpiredSession]);
  const download = () => {
    if (!report) return;
    const url = URL.createObjectURL(
      new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' }),
    );
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `operacao-${report.operation.id}-${report.generatedAt.replaceAll(':', '-')}.json`;
    anchor.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  const op = report?.operation;
  const zone = report?.schedule?.displayTimeZone ?? 'UTC';
  const date = (value: string | null | undefined) =>
    value
      ? new Intl.DateTimeFormat('pt-BR', {
          dateStyle: 'short',
          timeStyle: 'medium',
          timeZone: zone,
        }).format(new Date(value))
      : 'Não registrado';
  return (
    <section
      hidden={!active}
      id="operation-report"
      aria-labelledby="operation-report-title"
      className="operationReport"
    >
      <header className="reportHeader">
        <div>
          <span className="sectionEyebrow">Registro da operação</span>
          <h3 id="operation-report-title">Relatório operacional</h3>
          <p>
            Retrato dos registros no instante da emissão. O download preserva esta edição; novos
            adendos aparecem na próxima emissão.
          </p>
        </div>
        <div className="reportActions">
          <button type="button" onClick={() => setRefresh((n) => n + 1)}>
            Atualizar relatório
          </button>
          <button type="button" disabled={!report} onClick={download}>
            Baixar dados do relatório
          </button>
          <button type="button" disabled={!report} onClick={() => window.print()}>
            Imprimir / salvar PDF
          </button>
        </div>
      </header>
      {error ? (
        <p className="inlineError" role="alert">
          {error}
        </p>
      ) : !report ? (
        <p role="status">Preparando relatório…</p>
      ) : null}
      {report && op ? (
        <>
          <div className="reportIdentity">
            <h3>
              {op.referenceCode} · {op.missionName ?? 'Operação'}
            </h3>
            <p>
              {report.organization.name} · Emitido em {date(report.generatedAt)} · Fuso: {zone}
            </p>
            <p>
              {op.aircraft?.registrationMark ?? 'Aeronave não atribuída'} ·{' '}
              {op.base?.name ?? 'Base não atribuída'} · Responsável:{' '}
              {op.coordinator?.name ?? 'Não atribuído'}
            </p>
          </div>
          <dl className="reportStates">
            <div>
              <dt>Coordenação</dt>
              <dd>{label(op.state)}</dd>
            </div>
            <div>
              <dt>Decisão humana</dt>
              <dd>
                {op.operationalAuthorityDecisions[0]
                  ? label(op.operationalAuthorityDecisions[0].result)
                  : 'Não registrada'}
              </dd>
            </div>
            <div>
              <dt>Execução</dt>
              <dd>{label(op.executionStatus)}</dd>
            </div>
          </dl>
          <ReportSection title="Trechos e horários">
            <div className="reportTable">
              <table>
                <thead>
                  <tr>
                    <th>Trecho</th>
                    <th>Original</th>
                    <th>Revisado</th>
                    <th>Realizado</th>
                  </tr>
                </thead>
                <tbody>
                  {report.schedule.legs.map((leg) => (
                    <tr key={leg.id}>
                      <th>
                        {leg.sequence}. {leg.originName} → {leg.destinationName}
                        {leg.cancelledAt ? (
                          <small>Cancelado em {date(leg.cancelledAt)}</small>
                        ) : null}
                      </th>
                      <td>
                        {date(leg.plannedDeparture)}
                        <br />
                        {date(leg.plannedArrival)}
                      </td>
                      <td>
                        {date(leg.revisedDeparture)}
                        <br />
                        {date(leg.revisedArrival)}
                      </td>
                      <td>
                        {date(leg.actualDeparture)}
                        <br />
                        {date(leg.actualArrival)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </ReportSection>
          <ReportSection title="Checklist de coordenação">
            <p>
              {op.checklist
                ? `${op.checklist.templateName} · versão ${op.checklist.templateVersion}`
                : 'Sem checklist registrado'}
            </p>
            <ul>
              {op.checklist?.items.map((item) => (
                <li key={item.id}>
                  {item.completed ? 'Concluído' : 'Pendente'} · {item.title} ·{' '}
                  {item.required ? 'Obrigatório' : 'Opcional'}
                  {item.completedBy
                    ? ` · ${item.completedBy.name} · ${date(item.completedAt)}`
                    : ''}
                  {item.notes ? <p>{item.notes}</p> : null}
                </li>
              ))}
            </ul>
          </ReportSection>
          <ReportSection title="Pendências">
            <ul>
              {op.pendingItems.map((item) => (
                <li key={item.id}>
                  <strong>{item.title}</strong> · {label(item.severity)} · {label(item.status)}
                  <p>{item.detail}</p>
                  <p>
                    Responsável: {item.responsible?.name ?? 'Não atribuído'} · Prazo:{' '}
                    {date(item.dueAt)}
                  </p>
                  {item.resolutionNote ? (
                    <p>
                      {item.resolutionNote} · {item.resolvedBy?.name} · {date(item.resolvedAt)}
                    </p>
                  ) : null}
                </li>
              ))}
            </ul>
            {!op.pendingItems.length ? <p>Nenhuma pendência registrada.</p> : null}
          </ReportSection>
          <ReportSection title="Decisões de coordenação">
            <ul>
              {op.coordinationDecisions.map((item) => (
                <li key={item.id}>
                  <strong>{label(item.result)}</strong> · {item.decidedBy.name} ·{' '}
                  {label(item.authorityRoleKey)} · {date(item.decidedAt)}
                  <p>{item.justification}</p>
                </li>
              ))}
            </ul>
            {!op.coordinationDecisions.length ? <p>Nenhuma decisão registrada.</p> : null}
          </ReportSection>
          <ReportSection title="Decisões operacionais humanas">
            <ul>
              {op.operationalAuthorityDecisions.map((item) => (
                <li key={item.id}>
                  <strong>{label(item.result)}</strong> · {item.decidedBy.name} ·{' '}
                  {label(item.authorityRoleKey)} · {date(item.decidedAt)}
                  <p>Escopo: {item.scope}</p>
                  <p>{item.justification}</p>
                  {item.supersedesDecisionId ? (
                    <small>Revoga a decisão {item.supersedesDecisionId}</small>
                  ) : null}
                </li>
              ))}
            </ul>
            {!op.operationalAuthorityDecisions.length ? (
              <p>Nenhuma decisão registrada. O sistema não infere autorização.</p>
            ) : null}
          </ReportSection>
          <ReportSection title="Marcos de execução">
            <ul>
              {op.executionEvents.map((item) => (
                <li key={item.id}>
                  {label(item.eventType)} · {date(item.eventAt)} · {item.recordedBy.name}
                  {item.operationLegId
                    ? ` · Trecho ${op.legs.find((leg) => leg.id === item.operationLegId)?.sequence ?? item.operationLegId}`
                    : ''}
                  <p>{item.note}</p>
                </li>
              ))}
            </ul>
            {!op.executionEvents.length ? <p>Nenhum marco registrado.</p> : null}
          </ReportSection>
          <ReportSection title="Ocorrências">
            <ul>
              {op.occurrences.map((item) => (
                <li key={item.id}>
                  <strong>
                    {label(item.occurrenceType)} · {label(item.severity)}
                  </strong>{' '}
                  · {date(item.occurredAt)} · {item.reportedBy.name}
                  <p>{item.description}</p>
                  <p>Justificativa: {item.justification}</p>
                </li>
              ))}
            </ul>
            {!op.occurrences.length ? <p>Nenhuma ocorrência registrada.</p> : null}
          </ReportSection>
          <ReportSection title="Reprogramações e cancelamentos">
            <ul>
              {report.schedule.history.map((item) => (
                <li key={item.id}>
                  <strong>Revisão {item.revision}</strong> · {date(item.at)} · {item.actorName} ·{' '}
                  {label(item.actorRole)}
                  <p>{item.reason}</p>
                  {item.overrideJustification ? (
                    <p>Justificativa de conflito: {item.overrideJustification}</p>
                  ) : null}
                  <ul>
                    {item.after.legs?.map((leg) => (
                      <li key={leg.legId}>
                        Trecho {leg.sequence ?? leg.legId}:{' '}
                        {date(leg.revisedDeparture ?? leg.plannedDeparture)} →{' '}
                        {date(leg.revisedArrival ?? leg.plannedArrival)}
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
            <ul>
              {report.schedule.cancellations.map((item) => (
                <li key={item.id}>
                  <strong>Cancelamento</strong> · {date(item.at)} · {item.actorName} ·{' '}
                  {label(item.actorRole)}
                  <p>{item.reason}</p>
                  <p>Impacto: {item.impact}</p>
                </li>
              ))}
            </ul>
            {!report.schedule.history.length && !report.schedule.cancellations.length ? (
              <p>Nenhuma alteração de planejamento registrada.</p>
            ) : null}
          </ReportSection>
          <ReportSection title="Encerramento e adendos">
            {op.closure ? (
              <>
                <p>
                  <strong>{label(op.closure.result)}</strong> · {op.closure.closedBy.name} ·{' '}
                  {date(op.closure.closedAt)}
                </p>
                <p>{op.closure.summary}</p>
                <ul>
                  {op.closure.checklist.map((item) => (
                    <li key={String(item.key)}>
                      {String(item.label)} · {item.completed ? 'Conferido' : 'Pendente'}
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <p>Operação ainda não encerrada. Este relatório é parcial.</p>
            )}
            <ul>
              {op.amendments.map((item) => (
                <li key={item.id}>
                  <strong>Adendo</strong> · {item.createdBy.name} · {date(item.createdAt)}
                  <p>Motivo: {item.reason}</p>
                  <p>{item.correction}</p>
                </li>
              ))}
            </ul>
          </ReportSection>
          <footer>
            Relatório {report.reportVersion} · Operação {op.id} · Organização{' '}
            {report.organization.id}
          </footer>
        </>
      ) : null}
    </section>
  );
}
